import React, { useState, useEffect, useContext, useRef } from "react";
import PrimaryButton from "../Components/Button/PrimaryButton";
import PrimaryTextField from "../Components/PrimaryTextField/PrimaryTextField";
import { useTranslation } from "react-i18next";
import { GlobalStates } from "../GlobalStates";
import { Validate } from "../functionsIsmesti/validation";
import KalbuPasirinkimas from '../Components/KalbuPasirinkimas';
import Logo from "../assets/LOGO.png";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../services/supabaseClient";

const NewPass = () => {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(true);
    const [loading, setLoading] = useState(false);
    const { setAlert } = useContext(GlobalStates);
    const navigate = useNavigate();
    const location = useLocation();
    const [disabled, setDisabled] = useState(true);
    const [resetSuccess, setResetSuccess] = useState(false);
    
    const isActive = useRef(true);
    
    // Patikriname URL ir nustatome slaptažodžio keitimo režimą
    useEffect(() => {
        const isRecoveryFlow = 
            window.location.hash.includes('type=recovery') || 
            window.location.pathname.includes('auth/recovery') ||
            window.location.pathname.includes('NaujasSlaptazodis');
        
        if (isRecoveryFlow) {
            // Nustatome žymę, kad vyksta slaptažodžio keitimo procesas
            sessionStorage.setItem('password_reset_in_progress', 'true');
        }
    }, [location]);

    // Check if we're in a password reset flow and handle session accordingly
    useEffect(() => {
        const handlePasswordResetSession = async () => {
            try {
                // Check URL hash and pathname to identify recovery flow
                const isRecoveryFlow = 
                    window.location.hash.includes('type=recovery') || 
                    window.location.pathname.includes('auth/recovery') ||
                    window.location.pathname.includes('NaujasSlaptazodis');
                
                if (isRecoveryFlow) {
                    // Store flag in sessionStorage to prevent redirects
                    sessionStorage.setItem('password_reset_in_progress', 'true');
                    
                    // Don't sign out during password reset - let Supabase handle the session
                    // The password reset flow needs the session to work properly
                    return;
                }
                
                // If we're not in a recovery flow and have no session, redirect to login
                const { data } = await supabase.auth.getSession();
                if (!data.session && !isRecoveryFlow) {
                    navigate('/');
                }
            } catch (error) {
                console.error("Session check error:", error);
                // Don't redirect on error - better to show the form than redirect incorrectly
            }
        };
        
        // Run once on mount
        handlePasswordResetSession();
        
        // Cleanup function to remove the flag when component unmounts
        return () => {
            sessionStorage.removeItem('password_reset_in_progress');
        };
    }, [navigate]);

    // Papildomas efektas, kuris užtikrina, kad vartotojas nebūtų redirektinamas, kol pats nepasirinks
    useEffect(() => {
        // Funkcija, kuri periodiškai tikrina ir išlaiko slaptažodžio keitimo būseną
        const preventRedirect = () => {
            // Jeigu esame slaptažodžio keitimo puslapyje - palaikome žymę
            if (window.location.pathname.includes('NaujasSlaptazodis') || 
                window.location.pathname.includes('auth/recovery')) {
                    
                sessionStorage.setItem('password_reset_in_progress', 'true');
                
                // Papildomai patikriname, ar sesijos stebėjimas netrukdo slaptažodžio keitimui
                sessionStorage.setItem('password_reset_timeout', Date.now().toString());
            }
        };
        
        // Iškart paleidžiame funkciją, kad nereikėtų laukti 1s
        preventRedirect();
        
        // Nustatome intervalą, kuris tikrina kas pusę sekundės
        const intervalId = setInterval(preventRedirect, 500);
        
        // Išvalome intervalą, kai komponentas išmontuojamas
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    // Validate password without causing redirects
    useEffect(() => {
        // Don't run validation logic if component is unmounting
        let isActive = true;
        
        const validatePasswords = () => {
            try {
                // Start with disabled button
                if (!isActive) return;
                setDisabled(true);
                
                // Empty password is invalid
                if (password === '') {
                    return;
                }
                
                // Check password length
                const isValidPassword = Validate(password, 'password');
                if (!isValidPassword) {
                    return;
                }
                
                // Check if passwords match when both are entered
                if (confirmPassword && password !== confirmPassword) {
                    if (isActive) setPasswordsMatch(false);
                    return;
                }
                
                // All validations passed
                if (isActive) {
                    setPasswordsMatch(true);
                    setDisabled(false);
                }
            } catch (error) {
                console.error("Password validation error:", error);
                if (isActive) setDisabled(true);
            }
        };
        
        // Run validation
        validatePasswords();
        
        // Cleanup function to prevent state updates after unmount
        return () => {
            isActive = false;
        };
    }, [password, confirmPassword]);

    const handlePasswordChange = async (e) => {
        // Prevent form submission default behavior
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        
        // Don't proceed if button is disabled or already loading
        if (disabled || loading) return;
        
        setLoading(true);
        
        try {
            // Set the flag to prevent redirects during password change
            sessionStorage.setItem('password_reset_in_progress', 'true');
            
            // Update password directly with Supabase
            // When in recovery mode, Supabase expects the updateUser call
            const { error } = await supabase.auth.updateUser({
                password: password
            });
            
            if (error) {
                throw error;
            }
            
            // Show success message and set success state
            if (isActive.current) {
                setAlert(t('slaptazodisPakeistas'));
                setResetSuccess(true);
                
                // Mark that password was recently changed
                localStorage.setItem('password_recently_changed', 'true');
            }
        } catch (error) {
            if (isActive.current) {
                setAlert(error.message || t('klaida'));
            }
            console.error('Password update error:', error);
        } finally {
            // Remove the password reset flag
            sessionStorage.removeItem('password_reset_in_progress');
            setLoading(false);
        }
    };
    
    const handleGoToLogin = async () => {
        // Pažymime, kad slaptažodis neseniai pakeistas, kad prisijungimo komponentas galėtų atlikti specialius veiksmus
        localStorage.setItem('password_recently_changed', 'true');
        
        // Išvalome sesijos būsenas, susijusias su slaptažodžio keitimu
        sessionStorage.removeItem('password_reset_in_progress');
        sessionStorage.removeItem('password_reset_timeout');
        
        // Išvalome bet kokius cache duomenis
        try {
            // Išvalome visus slapukus, susijusius su autentifikacija
            document.cookie.split(';').forEach(cookie => {
                const name = cookie.trim().split('=')[0];
                if (name.includes('Formu') || name.includes('supabase')) {
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                }
            });
            
            // Išvalome kitą kešą
            for (let key in localStorage) {
                if (localStorage.hasOwnProperty(key) && 
                    key !== 'i18nextLng' && 
                    key !== 'password_recently_changed') {
                    localStorage.removeItem(key);
                }
            }
            
            // Išvalome visus session storage įrašus, išskyrus būtinus
            for (let key in sessionStorage) {
                if (sessionStorage.hasOwnProperty(key) && 
                    !key.startsWith('password_')) {
                    sessionStorage.removeItem(key);
                }
            }
            
            // Nutraukiame bėgiojančias sesijas pilnai atsijungiant
            await supabase.auth.signOut();
            
            // Palaukiame, kad būtų užtikrinta, jog išvalomi duomenys
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Nustatome location.replace vietoj navigate, kad visiškai išvalytume puslapio būseną
            window.location.replace(window.location.origin + '/');
        } catch (e) {
            console.error('Error during logout:', e);
            // Jei įvyko klaida, vis tiek bandome nukreipti su location.replace
            window.location.replace(window.location.origin + '/');
        }
    };

    useEffect(() => {
        isActive.current = true;
        return () => {
            isActive.current = false;
        };
    }, []);

    return (
        <div className='prisijungimoLangas'>
            <div className='prisijungimoKalbos'>
                <KalbuPasirinkimas
                    className='prisijungimoKalbos'
                    options={t('kalba')}
                    optionsText={t('kalba_text')}
                />
            </div>
            <div className='prisijungimasLabelMobile'>
                <h2 className='mainTxt2'>{t('slaptazodzio_priminimas')}</h2>
                <h5 className='mainTxtAprasymasMob1'>
                    {t('slaptazodzio_priminimas_txt')}
                </h5>
            </div>

            <div className='lentele'>
                <div className='lenteleVidus'>
                    <div className={'prisijungimasLabel'}>
                        <h2 className='mainTxt'>{t('slaptazodzio_priminimas')}</h2>
                        <h5 className='mainTxtAprasymas'>
                            {t('iveskite_nauja_pass')}
                        </h5>
                    </div>

                    <div className='prisijungimasLabelMobile'>
                        <h2 className={'mainTxt'}>{t('slaptazodzio_priminimas')}</h2>
                        <h5 className='mainTxtAprasymasMob txtAlignCenter'>
                            {t('iveskite_nauja_pass')}
                        </h5>
                    </div>
                    
                    {/* Use form to prevent accidental submissions */}
                    <form onSubmit={handlePasswordChange}>
                        <div>
                            <PrimaryTextField
                                label='new_login_pass'
                                onChange={(e) => { setPassword(e) }}
                                type={'password'}
                                fullWidth={true}
                                size='small'
                                classNameForm='txtFielfContainer reg'
                                validate={'password'}
                                errorMsg={t('iveskitePass')}
                            />
                        </div>
                        
                        <div style={{ marginTop: '15px' }}>
                            <PrimaryTextField
                                label='confirm_password'
                                onChange={(e) => { setConfirmPassword(e) }}
                                type={'password'}
                                fullWidth={true}
                                size='small'
                                classNameForm='txtFielfContainer reg'
                                validate={'password'}
                                errorMsg={!passwordsMatch ? t('passwordsDoNotMatch') : t('iveskitePass')}
                                error={!passwordsMatch}
                            />
                        </div>
                        
                        <div className='submitButtonCont'>
                            {!resetSuccess ? (
                                <PrimaryButton
                                    type="submit"
                                    disabled={disabled || loading}
                                    text={loading ? t('processing') : t('issaugoti')}
                                    onClick={handlePasswordChange}
                                    className='mobLogIn'
                                />
                            ) : (
                                <div style={{ textAlign: 'center' }}>
                                    <p style={{ marginBottom: '15px', color: 'green' }}>
                                        {t('slaptazodisPakeistas')}
                                    </p>
                                    <PrimaryButton
                                        text='prisijungti'
                                        onClick={handleGoToLogin}
                                        className='mobLogIn'
                                    />
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            <a href='http://www.ivairove.lt' target="_blank" className='loginLogoCont' rel="noreferrer">
                <img className='loginLogo' src={Logo} alt='logo' />
            </a>
        </div>
    );
}
export default NewPass
